// ============================================================================
// COOKIE CLEARING UTILITIES
// ============================================================================
//
// PURPOSE: Utility functions to clear browser cookies, particularly Supabase cookies
//
// FEATURES:
// - Clear specific Supabase cookies
// - Clear all cookies for the current domain
// - Clear cookies with specific patterns
// - Error handling and logging
//
// USAGE:
// - Clear Supabase cookies: clearSupabaseCookies()
// - Clear all cookies: clearAllCookies()
// - Clear cookies by pattern: clearCookiesByPattern('supabase')
//
// ============================================================================

import { debug } from "@/lib/utils/debug";

// ============================================================================
// TYPES
// ============================================================================

export interface ClearCookiesResult {
  success: boolean;
  clearedCount: number;
  error?: string;
}

// ============================================================================
// CORE COOKIE CLEARING FUNCTIONS
// ============================================================================

/**
 * Clear all cookies for the current domain
 */
export function clearAllCookies(): ClearCookiesResult {
  try {
    if (typeof document === "undefined") {
      debug.auth("clearAllCookies: Not in browser environment");
      return { success: false, clearedCount: 0, error: "Not in browser environment" };
    }

    const cookies = document.cookie.split(";");
    let clearedCount = 0;

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      if (name) {
        // Clear cookie by setting it to expire in the past
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
        clearedCount++;
      }
    });

    debug.auth(`Cleared ${clearedCount} cookies`);
    return { success: true, clearedCount };
  } catch (error) {
    debug.auth("Error clearing all cookies:", error);
    return {
      success: false,
      clearedCount: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Clear cookies that match a specific pattern
 */
export function clearCookiesByPattern(pattern: string): ClearCookiesResult {
  try {
    if (typeof document === "undefined") {
      debug.auth("clearCookiesByPattern: Not in browser environment");
      return { success: false, clearedCount: 0, error: "Not in browser environment" };
    }

    const cookies = document.cookie.split(";");
    let clearedCount = 0;

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      if (name && name.toLowerCase().includes(pattern.toLowerCase())) {
        // Clear cookie by setting it to expire in the past
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
        clearedCount++;
      }
    });

    debug.auth(`Cleared ${clearedCount} cookies matching pattern: ${pattern}`);
    return { success: true, clearedCount };
  } catch (error) {
    debug.auth("Error clearing cookies by pattern:", error);
    return {
      success: false,
      clearedCount: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Clear specific Supabase-related cookies
 */
export function clearSupabaseCookies(): ClearCookiesResult {
  try {
    if (typeof document === "undefined") {
      debug.auth("clearSupabaseCookies: Not in browser environment");
      return { success: false, clearedCount: 0, error: "Not in browser environment" };
    }

    // Common Supabase cookie patterns
    const supabasePatterns = [
      "sb-", // Supabase session cookies
      "supabase-", // Alternative Supabase cookie naming
      "supabase.auth.token", // Specific auth token cookie
    ];

    const cookies = document.cookie.split(";");
    let clearedCount = 0;

    cookies.forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      if (
        name &&
        supabasePatterns.some((pattern) => name.toLowerCase().includes(pattern.toLowerCase()))
      ) {
        // Clear cookie by setting it to expire in the past
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
        clearedCount++;
      }
    });

    debug.auth(`Cleared ${clearedCount} Supabase cookies`);
    return { success: true, clearedCount };
  } catch (error) {
    debug.auth("Error clearing Supabase cookies:", error);
    return {
      success: false,
      clearedCount: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Clear a specific cookie by name
 */
export function clearCookieByName(cookieName: string): ClearCookiesResult {
  try {
    if (typeof document === "undefined") {
      debug.auth("clearCookieByName: Not in browser environment");
      return { success: false, clearedCount: 0, error: "Not in browser environment" };
    }

    // Clear cookie by setting it to expire in the past
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;

    debug.auth(`Cleared cookie: ${cookieName}`);
    return { success: true, clearedCount: 1 };
  } catch (error) {
    debug.auth("Error clearing cookie by name:", error);
    return {
      success: false,
      clearedCount: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Clear all authentication-related cookies (Supabase + any other auth cookies)
 */
export function clearAuthCookies(): ClearCookiesResult {
  const supabaseResult = clearSupabaseCookies();

  // Also clear any other common auth cookie patterns
  const authPatterns = ["auth", "session", "token", "jwt"];
  let totalCleared = supabaseResult.clearedCount;

  authPatterns.forEach((pattern) => {
    const result = clearCookiesByPattern(pattern);
    totalCleared += result.clearedCount;
  });

  debug.auth(`Cleared ${totalCleared} total authentication cookies`);
  return {
    success: true,
    clearedCount: totalCleared,
  };
}

/**
 * Force clear all cookies and reload the page
 */
export function forceClearAllCookiesAndReload(): void {
  try {
    clearAllCookies();
    debug.auth("Force cleared all cookies, reloading page");
    window.location.reload();
  } catch (error) {
    debug.auth("Error in force clear and reload:", error);
    // Still try to reload even if clearing failed
    window.location.reload();
  }
}
